import type { RNG } from "@bing/engine-core";
import type { BgColor, BgConfig, BgSpot, BgStaged, BgState, BgView } from "./types";

/** Re-exported so callers of the rules layer get the canonical defaults from one place. */
export { DEFAULT_BG_CONFIG } from "./types";

/** A mutable working board (the pure move-gen mutates clones of this, never the input state). */
export interface Board {
  points: number[]; // length 24, signed
  bar: [number, number];
  off: [number, number];
}

/** +1 for seat 0 (positive counts), -1 for seat 1 (negative counts). */
export function signOf(color: BgColor): 1 | -1 {
  return color === 0 ? 1 : -1;
}

/** Seat 0 moves 23 -> 0 (index decreases); seat 1 moves 0 -> 23 (index increases). */
export function dirOf(color: BgColor): 1 | -1 {
  return color === 0 ? -1 : 1;
}

export function ownerAt(points: readonly number[], i: number): BgColor | null {
  const v = points[i]!;
  return v > 0 ? 0 : v < 0 ? 1 : null;
}

export function countAt(points: readonly number[], i: number): number {
  return Math.abs(points[i]!);
}

/** Landable by `color`: empty, own, or exactly one enemy (a blot — landing hits it). */
export function landable(points: readonly number[], i: number, color: BgColor): boolean {
  const owner = ownerAt(points, i);
  return owner === null || owner === color || countAt(points, i) === 1;
}

/** Seat 0 home = points 0..5; seat 1 home = points 18..23. */
export function inHome(color: BgColor, i: number): boolean {
  return color === 0 ? i >= 0 && i <= 5 : i >= 18 && i <= 23;
}

export function homePoints(color: BgColor): number[] {
  return color === 0 ? [0, 1, 2, 3, 4, 5] : [18, 19, 20, 21, 22, 23];
}

/** Entry point index for a barred checker of `color` on die `d` (1..6): seat 0 -> 24-d, seat 1 -> d-1. */
export function entryPoint(color: BgColor, d: number): number {
  return color === 0 ? 24 - d : d - 1;
}

/** Pips from point index `i` to bear off for `color` (bar = 25). Seat 0: i+1; seat 1: 24-i. */
export function pipToOff(color: BgColor, i: number): number {
  return color === 0 ? i + 1 : 24 - i;
}

/** Total pip count for `color` (sum of own distances + 25 per barred checker). */
export function pipCount(board: Board, color: BgColor): number {
  let pips = board.bar[color] * 25;
  for (let i = 0; i < 24; i++) {
    if (ownerAt(board.points, i) === color) pips += countAt(board.points, i) * pipToOff(color, i);
  }
  return pips;
}

export function cloneBoard(b: Board): Board {
  return { points: b.points.slice(), bar: [b.bar[0], b.bar[1]], off: [b.off[0], b.off[1]] };
}

/** Bearing off is legal only once every one of `color`'s checkers is home and none is on the bar. */
export function canBearOff(board: Board, color: BgColor): boolean {
  if (board.bar[color] > 0) return false;
  for (let i = 0; i < 24; i++) {
    if (ownerAt(board.points, i) === color && !inHome(color, i)) return false;
  }
  return true;
}

/** The largest pip-distance among `color`'s occupied points (0 if all borne off). Used by overshoot. */
export function highestHomePip(board: Board, color: BgColor): number {
  let max = 0;
  for (let i = 0; i < 24; i++) {
    if (ownerAt(board.points, i) === color) max = Math.max(max, pipToOff(color, i));
  }
  return max;
}

/**
 * The fixed 2-5-3-5 starting position (§5.1). Seat 0 moves 23 -> 0, so its back checkers sit on
 * the 24-point (index 23); seat 1 is the mirror. Relative (each player): 2 on the 24, 5 on the 13,
 * 3 on the 8, 5 on the 6. Absolute index = seat 0 uses `24 - rel`, seat 1 uses `rel - 1`.
 */
export function setupBoard(): Board {
  const points = Array<number>(24).fill(0);
  // Seat 0 (positive): rel 24/13/8/6 -> index 23/12/7/5.
  points[23] = 2;
  points[12] = 5;
  points[7] = 3;
  points[5] = 5;
  // Seat 1 (negative), mirrored: rel 24/13/8/6 -> index 0/11/16/18.
  points[0] = -2;
  points[11] = -5;
  points[16] = -3;
  points[18] = -5;
  return { points, bar: [0, 0], off: [0, 0] };
}

/**
 * The Iranian opening (§5.1). Each player throws ONE die; the higher number starts; a TIE is thrown
 * again until the two differ. **The starter does NOT re-roll** — the two dice already on the table
 * ARE their first turn: their own higher number and the opponent's lower one.
 *
 * ⚠️ CORRECTED 2026-08-14. This used to discard the contest and re-roll both dice for the winner, on
 * a note in the original spec claiming Iranian players find the two-singleton opening "wrong". That
 * was backwards, and the operator corrected it — «شخص آغازکننده با همان تاس ریخته شده بازی می‌کند و
 * دوباره تاس نمی‌ریزد (مثلاً اگر حریف ۲ آورده باشد و شما ۵، شما باید ۲ و ۵ را حرکت کنید)».
 *
 * A CONSEQUENCE worth knowing: because a tie is re-thrown, the two numbers always differ, so **the
 * opening turn can never be doubles** — the first turn is always exactly two moves, never four.
 *
 * Consumes `rng` deterministically so it is replayable from a seed.
 */
export function resolveOpeningRoll(rng: RNG): {
  turn: BgColor;
  dice: [number, number];
  /**
   * The two SINGLE dice of the DECIDING contest — seat 0's draw first, seat 1's second.
   *
   * These were always rolled here and then thrown away, which is why a game opened with dice
   * mysteriously already at rest. Surfacing them lets the client show the roll-off. A tied pair is
   * re-rolled, so what comes back is the pair that actually decided the turn, never a discarded
   * tie. Returning them adds NO `rng` draw, so every seed stays byte-identical.
   */
  contest: [number, number];
} {
  let a = rng.range(1, 6);
  let b = rng.range(1, 6);
  while (a === b) {
    a = rng.range(1, 6);
    b = rng.range(1, 6);
  }
  const turn: BgColor = a > b ? 0 : 1;
  // The starter's turn IS the contest: their own die first, the opponent's second. No re-roll, and
  // no extra `rng` draw — which is also why the two are never equal.
  const dice: [number, number] = a > b ? [a, b] : [b, a];
  return { turn, dice, contest: [a, b] };
}

/** One sub-move (alias of the staged shape). */
export type SubMove = BgStaged;

/**
 * The destination a single `die` reaches from `from` for `color`, or `null` if no legal sub-move.
 *
 *  - `from === "bar"`: enter on `entryPoint(color, die)` iff it is landable (else null).
 *  - a board point `i`: target `i + dirOf(color) * die`.
 *      · in range [0,23]  -> that point iff landable (empty / own / a lone enemy = hit).
 *      · off the edge      -> bearing off (only when `canBearOff`): exact pip match -> "off";
 *        an overshoot (pip < die) -> "off" ONLY when `i` is the highest occupied home point.
 *
 * `mustBearOffWhenPossible` (default OFF): when a checker can bear off with this die, forbid the
 * (otherwise-legal) within-home move — an Iranian house-rule nuance kept off by default (standard).
 */
export function subMoveTarget(
  board: Board,
  color: BgColor,
  from: BgSpot,
  die: number,
  config: BgConfig,
): BgSpot | null {
  if (from === "bar") {
    if (board.bar[color] <= 0) return null;
    const entry = entryPoint(color, die);
    return landable(board.points, entry, color) ? entry : null;
  }
  if (from === "off") return null;
  const i = from;
  if (ownerAt(board.points, i) !== color) return null; // not your checker
  const t = i + dirOf(color) * die;
  if (t >= 0 && t <= 23) {
    if (!landable(board.points, t, color)) return null;
    // `mustBearOffWhenPossible`: if this checker could also bear off with this die, forbid the
    // within-home move. (Only relevant while bearing off; harmless otherwise.)
    if (config.mustBearOffWhenPossible && canBearOff(board, color)) {
      const pip = pipToOff(color, i);
      if (pip === die || (pip < die && pip === highestHomePip(board, color))) return null;
    }
    return t;
  }
  // Off the edge -> bearing off.
  if (!canBearOff(board, color)) return null;
  const pip = pipToOff(color, i);
  if (pip === die) return "off"; // exact
  if (pip < die) return pip === highestHomePip(board, color) ? "off" : null; // overshoot from the highest
  return null; // pip > die can't leave the board (target would be in range) — unreachable here
}

/** True iff landing on `to` HITS an enemy blot sitting inside `color`'s own home board (§5.2). */
export function willHitInHome(board: Board, color: BgColor, to: BgSpot): boolean {
  if (to === "off" || to === "bar") return false;
  const opp: BgColor = color === 0 ? 1 : 0;
  return inHome(color, to) && ownerAt(board.points, to) === opp && countAt(board.points, to) === 1;
}

/** Apply one sub-move IN PLACE: lift from source, hit an enemy blot (-> bar), place on dest / bear off. */
export function applySubMove(board: Board, color: BgColor, sub: SubMove): void {
  const sign = signOf(color);
  const opp: BgColor = color === 0 ? 1 : 0;
  // Lift from source.
  if (sub.from === "bar") board.bar[color] -= 1;
  else if (sub.from !== "off") board.points[sub.from] = board.points[sub.from]! - sign;
  // Place on destination.
  if (sub.to === "off") {
    board.off[color] += 1;
    return;
  }
  if (sub.to === "bar") return; // (never used as a `to` for a normal move)
  const i = sub.to;
  if (ownerAt(board.points, i) === opp && countAt(board.points, i) === 1) {
    board.points[i] = 0; // clear the hit blot
    board.bar[opp] += 1;
  }
  board.points[i] = board.points[i]! + sign;
}

/** Point index -> number of checkers frozen there this turn (the no-hit-and-run hitters, §5.2). */
export type FrozenMap = Map<number, number>;

/**
 * The legal single-die sub-moves from `board` for `color`, enforcing:
 *  - **bar-first priority (§5.3):** while any checker is on the bar, ONLY a bar entry is offered.
 *  - **frozen exclusion (§5.2):** a point with all its checkers frozen cannot be a source.
 * Returns `[]` when the die is unplayable. When this holds for EVERY die of the roll, the turn dances.
 *
 * SCOPE — this is the per-die PRIMITIVE only. It deliberately does NOT enforce the §5.4 maximal-play
 * rule (must-use-both / must-use-larger): that is applied one level up by the move-SEQUENCE generator
 * (`enumerateTurns` — a later task), which iterates this per remaining die, recurses on a clone, and
 * keeps only the maximal sequences. Do not add maximal-play logic here.
 */
export function subMovesForDie(
  board: Board,
  color: BgColor,
  die: number,
  frozen: FrozenMap,
  config: BgConfig,
): SubMove[] {
  if (board.bar[color] > 0) {
    const to = subMoveTarget(board, color, "bar", die, config);
    return to === null ? [] : [{ from: "bar", to, die }];
  }
  const out: SubMove[] = [];
  for (let i = 0; i < 24; i++) {
    if (ownerAt(board.points, i) !== color) continue;
    const available = countAt(board.points, i) - (frozen.get(i) ?? 0);
    if (available < 1) continue; // every checker here is frozen
    const to = subMoveTarget(board, color, i, die, config);
    if (to !== null) out.push({ from: i, to, die });
  }
  return out;
}

/**
 * NO ROLL CAN PLAY — not one die from 1 to 6 moves any of `color`'s checkers, so every one of the 21
 * rolls would dance. card-98 item 6 (and the operator's 6.3, 2026-10-28): such a turn is not rolled
 * at all — it passes on its own (see `systemStepDelayMs` in ./engine). Two shapes reach it:
 *
 *   · CLOSED OUT — a checker on the bar and all six entry points held (bar-first, §5.3, then
 *     forbids every other checker);
 *   · a FULL PRIME with nothing on the bar — every checker sits where each die lands on a held point.
 *
 * Asking per DIE is exact: any legal turn begins with a single-die sub-move, and if one die can move
 * anything then the roll of that die (doubled, if need be) has a non-empty maximal turn (§5.4 only
 * filters among non-empty ones). §5.2's freeze is a mid-turn rule, so nothing is frozen here.
 */
export function noRollCanPlay(board: Board, color: BgColor, config: BgConfig): boolean {
  const nothingFrozen: FrozenMap = new Map();
  for (let d = 1; d <= 6; d++) {
    if (subMovesForDie(board, color, d, nothingFrozen, config).length > 0) return false;
  }
  return true;
}

/** A full legal play: an ordered sub-move list and the board it produces. */
export interface Turn {
  subMoves: SubMove[];
  result: Board;
}

/** Expand a roll into the dice actually played: a double is four of the value, else the pair. */
export function expandRoll(a: number, b: number): number[] {
  return a === b ? [a, a, a, a] : [a, b];
}

/** A canonical string signature of a board (for de-duplicating resulting positions). */
export function boardKey(b: Board): string {
  return `${b.points.join(",")}|${b.bar[0]},${b.bar[1]}|${b.off[0]},${b.off[1]}`;
}

function removeOneDie(remaining: readonly number[], d: number): number[] {
  const out = remaining.slice();
  out.splice(out.indexOf(d), 1);
  return out;
}

function uniqueDice(remaining: readonly number[]): number[] {
  return [...new Set(remaining)];
}

/**
 * Every legal ORDERED sub-move sequence for `color` from `board`, given the expanded `dice`.
 * DFS: at each node, for each distinct remaining die, for each legal source, apply the sub-move,
 * freeze the hitter on an in-home hit (§5.2), and recurse on the reduced dice. A node that can
 * extend nothing is a terminal turn. A total dance yields a single empty turn. Bar-first, combined
 * intermediates, doubles (up to 4) and bearing off all fall out of `subMovesForDie` + `subMoveTarget`.
 *
 * SCOPE — this is the RAW ordered enumeration (every terminal DFS path). It does NOT filter to the
 * §5.4 maximal-play set (must-use-both / must-use-larger); that selection is the consumer's job
 * (compare `subMoves.length`, keeping only the longest, and — on a length tie of 1 — the larger die).
 */
export function enumerateTurns(
  board: Board,
  color: BgColor,
  dice: readonly number[],
  config: BgConfig,
): Turn[] {
  const results: Turn[] = [];
  const NOTHING_FROZEN: FrozenMap = new Map();
  /*
   * §5.2's TWO DOCUMENTED IRANIAN EXCEPTIONS to «زدن و بلند شدن» (2026-08-14).
   *
   * EXCEPTION 1 — DOUBLES. «زدن مهرهٔ حریف در داخل شش خانهٔ خودی مجاز نیست، مگر اینکه با تاس جفت
   * این کار را انجام دهید.» A double is the only roll `expandRoll` expands to four, so the whole
   * turn is simply exempt: nothing freezes.
   */
  const doubles = dice.length === 4;
  const banActive = config.noHitAndRunInHome && !doubles;

  const recurse = (b: Board, remaining: number[], path: SubMove[], frozen: FrozenMap): void => {
    const available = uniqueDice(remaining);
    const offer = (map: FrozenMap): { die: number; sub: SubMove }[] =>
      available.flatMap((d) =>
        subMovesForDie(b, color, d, map, config).map((sub) => ({ die: d, sub })),
      );

    let options = offer(frozen);
    /*
     * EXCEPTION 2 — NO OTHER MOVE. «تنها در صورتی که بازیکن هیچ حرکت دیگری نداشته باشد مجاز به
     * «زدن و بلند شدن» خواهد بود.» The ban is a RESTRICTION, and a restriction that would leave a
     * player unable to move at all lifts. Asked exactly as the rule words it — «has no other move»,
     * evaluated against the position in front of them, for every remaining die.
     *
     * ⚠️ The sources are silent on one edge and this is the reading we ship: the exemption belongs
     * to the line the player is actually IN, so a player who had a different (non-hitting) line
     * available still gets it on the line where they chose to hit. `maximalTurns` then keeps
     * whichever line uses the most dice, so in the rare case the hitting line ends up strictly
     * longer, §5.4 will require it.
     */
    if (options.length === 0 && frozen.size > 0) options = offer(NOTHING_FROZEN);

    if (options.length === 0) {
      results.push({ subMoves: path, result: b });
      return;
    }
    for (const { die, sub } of options) {
      const nb = cloneBoard(b);
      const froze = banActive && willHitInHome(b, color, sub.to);
      applySubMove(nb, color, sub);
      const nextFrozen: FrozenMap = new Map(frozen);
      if (froze && typeof sub.to === "number") {
        nextFrozen.set(sub.to, (nextFrozen.get(sub.to) ?? 0) + 1);
      }
      recurse(nb, removeOneDie(remaining, die), [...path, sub], nextFrozen);
    }
  };
  recurse(cloneBoard(board), dice.slice(), [], new Map());
  return results;
}

/**
 * The §5.4 maximal-play filter. Keep only the sequences using the MOST dice (this one rule enforces
 * both "use both numbers" and "use all four on a double"). When the maximum length is exactly 1 and
 * the roll is a NON-double, apply the larger-die tiebreak: if the larger die is individually playable,
 * discard turns that played only the smaller one ("must play the larger"). When neither number can be
 * used, the single empty turn (length 0) survives — the turn dances.
 */
export function maximalTurns(turns: readonly Turn[], dice: readonly number[]): Turn[] {
  const max = turns.reduce((m, t) => Math.max(m, t.subMoves.length), 0);
  const maximal = turns.filter((t) => t.subMoves.length === max);
  const distinct = [...new Set(dice)];
  if (max === 1 && distinct.length === 2) {
    const hi = Math.max(distinct[0]!, distinct[1]!);
    const hiTurns = maximal.filter((t) => t.subMoves[0]!.die === hi);
    if (hiTurns.length > 0) return hiTurns; // the larger die is playable -> it must be played
  }
  return maximal;
}

/** The legal MAXIMAL full turns from `board` for `color` given the expanded `dice`. */
export function legalTurns(
  board: Board,
  color: BgColor,
  dice: readonly number[],
  config: BgConfig,
): Turn[] {
  return maximalTurns(enumerateTurns(board, color, dice, config), dice);
}

export function subMoveEq(a: SubMove, b: SubMove): boolean {
  return a.from === b.from && a.to === b.to && a.die === b.die;
}

/**
 * The legal NEXT sub-move set given a `staged` prefix: enumerate the maximal turns from the COMMITTED
 * board, keep those whose first `staged.length` sub-moves equal `staged`, and return the (deduped)
 * `staged.length`-th sub-move of each survivor. Empty when the turn is maximally staged (only
 * `confirm` remains) or when the roll dances.
 */
export function legalNextSubMoves(
  committed: Board,
  color: BgColor,
  dice: readonly number[],
  staged: readonly SubMove[],
  config: BgConfig,
): SubMove[] {
  const turns = legalTurns(committed, color, dice, config);
  const out: SubMove[] = [];
  for (const t of turns) {
    if (t.subMoves.length <= staged.length) continue;
    let matches = true;
    for (let k = 0; k < staged.length; k++) {
      if (!subMoveEq(t.subMoves[k]!, staged[k]!)) {
        matches = false;
        break;
      }
    }
    if (!matches) continue;
    const nextSub = t.subMoves[staged.length]!;
    if (!out.some((s) => subMoveEq(s, nextSub))) out.push(nextSub);
  }
  return out;
}

/**
 * The points whose checkers the §5.2 ban is withholding from `color` RIGHT NOW — the authoritative
 * answer to «which checker is locked», for the board to mark and explain.
 *
 * ⚠️ THIS IS DELIBERATELY NOT A RE-IMPLEMENTATION OF THE FROZEN MAP. The client used to decide this
 * geometrically ("was it a hit, and did it land in the mover's home?"), which is wrong three ways at
 * once: it fires on a DOUBLE (where the ban is inactive for the whole turn), it fires when EXCEPTION 2
 * has lifted the ban, and it fires when the turn has no dice left to withhold anything with. The
 * honest question is «is the BAN the reason this checker cannot move?», so that is what is asked:
 *
 *   offered with the ban OFF  ∧  withheld with the ban ON  ⇒  frozen
 *
 * Asking it that way gets both exceptions and the no-dice-left case for free, because they are
 * already inside `legalNextSubMoves`, and it can never call a checker locked when what is really
 * stopping it is the geometry (a blocked destination), because then it is withheld BOTH ways.
 */
export interface BgFrozen {
  /** Points the ban is withholding RIGHT NOW: the checker is marked and cannot move again this turn. */
  readonly locked: readonly number[];
  /**
   * Points where the hitter would be locked but EXCEPTION 2 has freed it — the player has no other
   * move, so §5.2 lifts. Reported separately because it is the one exception a player can SEE
   * happening: they hit inside their own home and the checker moves anyway.
   */
  readonly lifted: readonly number[];
}

const NOTHING_FROZEN_RESULT: BgFrozen = { locked: [], lifted: [] };

export function frozenPoints(
  committed: Board,
  color: BgColor,
  dice: readonly number[],
  staged: readonly SubMove[],
  config: BgConfig,
): BgFrozen {
  // EXCEPTION 1 — a double exempts the entire turn, so nothing can be frozen. Checked up front
  // because it also spares the common case a second enumeration.
  if (!config.noHitAndRunInHome || dice.length === 4) return NOTHING_FROZEN_RESULT;
  // Candidates: the points this seat hit INSIDE ITS OWN HOME earlier in this very turn.
  const b = cloneBoard(committed);
  const hits: number[] = [];
  for (const s of staged) {
    if (typeof s.to === "number" && willHitInHome(b, color, s.to)) hits.push(s.to);
    applySubMove(b, color, s);
  }
  if (hits.length === 0) return NOTHING_FROZEN_RESULT;
  const sources = (cfg: BgConfig): Set<BgSpot> =>
    new Set(legalNextSubMoves(committed, color, dice, staged, cfg).map((s) => s.from));
  const withBan = sources(config);
  const withoutBan = sources({ ...config, noHitAndRunInHome: false });
  return {
    locked: hits.filter((p) => !withBan.has(p) && withoutBan.has(p)),
    /*
     * A candidate is ALWAYS in the frozen map (the ban is active and it hit inside its own home),
     * so the only way the enumeration can still offer it is exception 2 having lifted the ban.
     */
    lifted: hits.filter((p) => withBan.has(p)),
  };
}

/** The display / confirm board: the committed board with `staged` applied in order. */
export function applyStaged(committed: Board, color: BgColor, staged: readonly SubMove[]): Board {
  const b = cloneBoard(committed);
  for (const s of staged) applySubMove(b, color, s);
  return b;
}

/** The dice still playable this turn = the full roll minus the dice already staged (multiset). */
export function diceRemaining(dice: readonly number[], staged: readonly SubMove[]): number[] {
  const out = dice.slice();
  for (const s of staged) {
    const idx = out.indexOf(s.die);
    if (idx >= 0) out.splice(idx, 1);
  }
  return out;
}

/** Reconstruct a working board from a view (perfect info). Used by the bot at `staged`-empty. */
export function boardFromView(view: BgView): Board {
  return {
    points: view.points.slice(),
    bar: [view.bar[0], view.bar[1]],
    off: [view.off[0], view.off[1]],
  };
}

/** Reconstruct a working board from the COMMITTED engine state (no staged applied). */
export function boardFromState(state: BgState): Board {
  return {
    points: state.points.slice(),
    bar: [state.bar[0], state.bar[1]],
    off: [state.off[0], state.off[1]],
  };
}

/**
 * Win magnitude — 1 عادی / 2 مارس / 3 سگ‌مارس.
 *
 *  · عادی  the loser has borne off at least one checker.
 *  · مارس  the loser has borne off NONE.
 *  · سگ‌مارس  the loser has borne off none AND still has a checker on the bar, or one sitting
 *    inside the WINNER's home board («در زمین حریف»).
 */
export function magnitudeOf(board: Board, winner: BgColor, config: BgConfig): 1 | 2 | 3 {
  const loser: BgColor = winner === 0 ? 1 : 0;
  if (board.off[loser] > 0) return 1;
  if (config.countSagMars) {
    if (board.bar[loser] > 0) return 3;
    for (let i = 0; i < 24; i++) {
      if (ownerAt(board.points, i) === loser && inHome(winner, i)) return 3;
    }
  }
  return 2;
}
